import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {ActivityHeaderHeadingProps} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ActivityHeader, CommentAuthorTypes} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CommentActions} from '@github-ui/commenting/CommentActions'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {VALUES} from '@github-ui/commenting/Values'
import {noop} from '@github-ui/noop'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'
import {StopIcon} from '@primer/octicons-react'
import {Flash} from '@primer/react'
import {useRef, useState} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCommentAnchor} from '../../hooks/use-comment-anchor'
import type {Comment, ViewerData} from '../../conversation-types'
import styles from './AutomatedReviewThreadComment.module.css'
import {AutomatedReviewSuggestion} from './AutomatedReview/AutomatedReviewSuggestion'
import {getBodyForClipboard} from '../../helpers/get-body-for-clipboard'

type CommentState = 'editing' | 'hidden' | 'visible'

export interface AutomatedReviewThreadCommentProps {
  anchorPrefix?: string
  basePath?: string
  comment: Comment
  /**
   * If true, the comment will scroll itself into view and show a blue border
   * when the component renders with a hash that matches the comment.
   */
  isAnchorable?: boolean
  isInlineComment: boolean
  isInDialogMode?: boolean | undefined
  isOutdated?: boolean
  isThreadResolved: boolean
  onQuoteReply?: (quotedText?: string) => void
  subjectId: string
  threadId: string
  threadPositionNumber?: number
  threadsConnectionId?: string
  viewerData?: ViewerData
  originalDiffPathUri?: string | null
}

function formatBotAuthorLogin(login?: string): string | undefined {
  if (!login) {
    return undefined
  }

  // Change the format of bots (eg.: `github-code-quality[bot]` to `github-code-quality`)
  return login.replace(/\[bot\]$/i, '')
}

export function AutomatedReviewThreadComment({
  anchorPrefix = 'r',
  basePath,
  comment,
  isAnchorable = false,
  isInDialogMode,
  isOutdated,
  onQuoteReply = noop,
  threadId,
  threadPositionNumber,
  originalDiffPathUri,
}: AutomatedReviewThreadCommentProps): React.ReactElement {
  const [isMinimized, setIsMinimized] = useState(comment.isHidden)
  const [visibilityErrorMessage, _] = useState('')

  const commentBodyRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    id: automatedCommentId,
    title,
    message,
    severity,
    suggestion,
    suggestionState,
    feedbackOptions,
  } = comment.automatedComment || {}

  const isSimplificationEnabled =
    useFeatureFlag('automated_review_comment_simplification') ||
    isFeatureEnabled('automated_review_comment_simplification')

  // TODO(@aliceclv): figure out when the `.id` value gets populated to understand if it's a safe fallback
  // The unique identifier for this comment in the DOM
  const commentId = comment.databaseId?.toString() || comment.id
  // The anchor portion of the URL that will target this specific comment when navigating to it
  // This is used to scroll the comment into view and highlight it when directly linked
  const commentAnchor = commentId ? `${anchorPrefix}${commentId}` : undefined
  // Note(@aliceclv): Comment is not editable
  const commentState: CommentState = isMinimized ? 'hidden' : 'visible'

  // The full URL including anchor that points directly to this comment within the current page
  const pageScopedCommentHref =
    commentAnchor && ssrSafeLocation
      ? new URL(`${ssrSafeLocation.pathname}#${commentAnchor}`, ssrSafeLocation.origin).toString()
      : ''

  useCommentAnchor({
    isAnchorable,
    commentId,
    commentAnchor,
    containerRef,
  })

  const commentActivityProps = {
    headingProps: {as: 'h3'} as ActivityHeaderHeadingProps,
    avatarUrl: comment.author?.avatarUrl ?? VALUES.ghostUser.avatarUrl,
    comment: {
      ...comment,
      bodyForClipboard: getBodyForClipboard({comment}),
      url: pageScopedCommentHref,
      referenceText: comment.reference.text ?? `#${comment.reference.number}`,
      // Note(@aliceclv): none of these actions are allowed in the context of an automated comment
      viewerCanMinimize: false,
      viewerCanUpdate: false,
      viewerCanDelete: false,
      viewerCanBlockFromOrg: false,
      viewerCanUnblockFromOrg: false,
      viewerCanReport: false,
      viewerCanReportToMaintainer: false,
      viewerCanReferenceInIssue: false,
      viewerCanQuoteReply: false,
    },
    commentRef: containerRef,
    editHistoryComponent: undefined,
    onBlock: undefined,
    onUnblock: undefined,
    onMinimize: setIsMinimized,
    onSuccessfulBlock: () => {
      //not ideal, but this is the easiest way to get all of the comments to reflect the newly updated state
      setTimeout(() => ssrSafeWindow?.location.reload(), 800)
    },
    isMinimized,
    navigate: noop,
    onReplySelect: onQuoteReply,
    isReply: false,
    forceInlineAvatar: true,
    containerStyle: {},
    originalDiffPathUri,
    isOutdated,
    // Comment actions are null as they are not allowed for automated comments
    editComment: () => null,
    deleteComment: () => null,
    hideComment: () => null,
    unhideComment: () => null,

    // Comment author props
    commentAuthorSlug: undefined,
    commentAuthorType: CommentAuthorTypes.Bot,
    commentAuthorLogin: formatBotAuthorLogin(comment.author?.login) ?? VALUES.ghostUser.login,
    hideProfileLink: true,
  }

  const getCommentAriaLabel = (): string => {
    if (threadPositionNumber !== undefined) {
      return `Comment ${threadPositionNumber}`
    } else {
      return 'Comment'
    }
  }

  return (
    <div
      ref={containerRef}
      id={isAnchorable ? commentAnchor : undefined}
      className={styles.automatedComment}
      style={{scrollMarginTop: isAnchorable ? '120px' : 0}}
      data-first-thread-comment
      data-marker-navigation-comment-thread-id={threadId}
      data-marker-navigation-comment-id={comment.id}
      data-testid="automated-review-comment"
      // When aria-label and role is set while still in gridcell mode, the browser doesn't the respect the
      // aria-hidden"true" and we end up with the aria-labels leaking into the accessible name of the gridcell,
      // which is not what we want. So we should only set these attributes while in dialog mode.
      {...(isInDialogMode
        ? {
            role: 'document',
            'aria-roledescription': 'comment',
            'aria-label': getCommentAriaLabel(),
          }
        : {})}
    >
      <div className={styles.automatedComment__headerContainer}>
        <ActivityHeader
          {...commentActivityProps}
          commentBody={comment.body}
          isInDialogMode={isInDialogMode}
          actions={<CommentActions {...commentActivityProps} />}
          isOutdated={isOutdated}
        />
      </div>
      {visibilityErrorMessage && (
        <Flash variant="danger" className="m-3">
          <StopIcon className="mr-2" />
          {visibilityErrorMessage}
        </Flash>
      )}
      <div className={styles.automatedComment__bodyContainer}>
        {commentState === 'visible' && (
          <div className={styles.automatedComment__body}>
            <div
              className={`${styles.automatedComment__content} ${
                !isSimplificationEnabled && suggestion ? styles.automatedComment__withBorderBottom : ''
              }`}
            >
              <div className="d-flex flex-items-center">
                <h3 className={styles.automatedComment__title}>{title}</h3>
                {severity && <RuleSeverityBadge severity={severity} />}
              </div>
              <VerifiedHTMLText
                ref={commentBodyRef}
                className={`${styles.automatedComment__message} markdown-body`}
                comment-testid={`Comment message html for automated comment ${commentId}`}
                html={message as SafeHTMLString}
              />
            </div>
            {automatedCommentId && (
              <AutomatedReviewSuggestion
                commentTitle={title}
                commentAuthorLogin={comment.author?.login}
                feedbackOptions={feedbackOptions}
                suggestion={suggestion}
                suggestionState={suggestionState}
                automatedCommentId={automatedCommentId}
                basePath={basePath}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

try{ AutomatedReviewThreadComment.displayName ||= 'AutomatedReviewThreadComment' } catch {}